import {
  Briefcase,
  Compass,
  FileText,
  Globe,
  MessageCircle,
  Mic,
  User,
  Users,
  Zap,
} from "lucide-react";

import {
  KnowledgeSource,
  PersonalityDimension,
  PersonalityPreset,
  WizardStepMeta,
} from "@/types/mockups/genie-setup-v2";

export const wizardSteps: WizardStepMeta[] = [
  {
    id: "job",
    label: "Job",
    question: "What is this genie for?",
    description: "Pick the main thing this genie should do.",
  },
  {
    id: "personality",
    label: "Personality",
    question: "How should it sound?",
    description: "Choose how your genie speaks. You'll see a live example as you pick.",
  },
  {
    id: "playbooks",
    label: "Playbooks",
    question: "What specific flows can it run?",
    description: "Add structured plays your genie can follow.",
  },
  {
    id: "knowledge",
    label: "Knowledge",
    question: "What does it know?",
    description: "Connect knowledge sources and add any context only you know.",
  },
  {
    id: "preview",
    label: "Test",
    question: "Try it out",
    description: "Send a sample message and see exactly how your genie responds.",
  },
  {
    id: "review",
    label: "Review",
    question: "Ready to deploy?",
    description: "Final check before your genie goes live.",
  },
];

export const personalityDimensions: PersonalityDimension[] = [
  {
    id: "role",
    label: "Role",
    question: "How does your genie show up?",
    helper: "The hat your genie wears in every conversation.",
    icon: Briefcase,
    options: [
      {
        id: "concierge",
        label: "Concierge",
        description: "Polite, helpful, gets things done.",
        examplePreview:
          "Of course — I'd be happy to help with that. Let me grab the details for you right now.",
        promptFragment: "Adopt the role of a polished concierge. Be helpful and efficient.",
      },
      {
        id: "coach",
        label: "Coach",
        description: "Encourages, motivates, drives action.",
        examplePreview:
          "Great question — let's tackle this together. First thing I'd do is open up the dashboard.",
        promptFragment: "Adopt the role of an encouraging coach who motivates and guides the user.",
      },
      {
        id: "expert",
        label: "Expert",
        description: "Knowledgeable, authoritative, precise.",
        examplePreview:
          "Based on your setup, the recommended approach is option B — here's exactly why.",
        promptFragment: "Adopt the role of a domain expert who gives precise, authoritative answers.",
      },
      {
        id: "friend",
        label: "Friend",
        description: "Warm, casual, easy to talk to.",
        examplePreview: "Hey! Sure thing — happy to walk you through it. Got a sec?",
        promptFragment: "Adopt the role of a warm, casual friend who chats naturally.",
      },
      {
        id: "trainer",
        label: "Trainer",
        description: "Teaches, checks understanding, builds skill.",
        examplePreview:
          "Let's break this down. First, do you know what the dashboard does? Once that's clear, we'll move on.",
        promptFragment:
          "Adopt the role of a patient trainer. Teach step by step and check the user's understanding.",
      },
    ],
  },
  {
    id: "length",
    label: "Response length",
    question: "How much does your genie say?",
    helper: "Short feels snappy. Long feels thorough. Pick the vibe.",
    icon: FileText,
    options: [
      {
        id: "short",
        label: "Short & sharp",
        description: "One or two sentences. Always.",
        examplePreview: "Got it — tomorrow at 3pm works. Confirmed.",
        promptFragment: "Keep every response to one or two short sentences. No fluff.",
      },
      {
        id: "balanced",
        label: "Balanced",
        description: "A few sentences when needed.",
        examplePreview:
          "Tomorrow at 3pm works perfectly. I've put it in the calendar and sent you a confirmation. Anything else?",
        promptFragment: "Aim for two to four sentence responses. Cover the answer without padding.",
      },
      {
        id: "detailed",
        label: "Detailed walkthrough",
        description: "Full explanations and context.",
        examplePreview:
          "Great — I can confirm tomorrow at 3pm. I've added it to your calendar and sent a confirmation to the email on file. You'll also get a reminder one hour before. Would you like me to share any prep notes?",
        promptFragment:
          "Give thorough, multi-sentence responses with context, examples, and next-step suggestions.",
      },
    ],
  },
  {
    id: "energy",
    label: "Energy",
    question: "What's the emotional pitch?",
    helper: "How animated does your genie feel when speaking?",
    icon: Zap,
    options: [
      {
        id: "excited",
        label: "Excited",
        description: "Bright, enthusiastic, peppy.",
        examplePreview: "Oh awesome — yes, let's do it! I'll get that started for you right now.",
        promptFragment: "Speak with bright, enthusiastic energy. Use upbeat, positive language.",
      },
      {
        id: "warm",
        label: "Warm & steady",
        description: "Friendly without being over the top.",
        examplePreview: "Of course, happy to help with that — give me one moment.",
        promptFragment: "Speak with a warm, steady tone. Be friendly without being effusive.",
      },
      {
        id: "calm",
        label: "Calm & measured",
        description: "Composed, grounded, no urgency.",
        examplePreview: "Certainly. I'll take care of that for you.",
        promptFragment: "Speak calmly and with measured pace. Stay composed and grounded.",
      },
    ],
  },
  {
    id: "approach",
    label: "Approach",
    question: "How does it engage?",
    helper: "Does it answer directly, listen first, or ask questions back?",
    icon: Compass,
    options: [
      {
        id: "direct",
        label: "Direct answers",
        description: "Cuts to the chase.",
        examplePreview: "Yes — reset your password from Settings > Security. Takes 30 seconds.",
        promptFragment: "Answer the user's question directly. Lead with the answer, then context.",
      },
      {
        id: "empathetic",
        label: "Empathetic listener",
        description: "Acknowledges first, then helps.",
        examplePreview:
          "That sounds frustrating — let's get it sorted. You can reset your password from Settings > Security.",
        promptFragment:
          "Acknowledge the user's feelings or situation before providing the answer. Show empathy.",
      },
      {
        id: "socratic",
        label: "Socratic",
        description: "Asks questions back to draw out understanding.",
        examplePreview:
          "Good question — before I jump in, do you remember the email you signed up with? That'll shape what we try first.",
        promptFragment:
          "Engage Socratically — ask clarifying questions that lead the user toward the answer themselves.",
      },
    ],
  },
  {
    id: "formality",
    label: "Formality",
    question: "How formal is the language?",
    helper: "Casual feels human. Formal feels professional.",
    icon: User,
    options: [
      {
        id: "casual",
        label: "Casual",
        description: "Contractions, light slang, real talk.",
        examplePreview: "Yeah no worries — I'll sort that out for you.",
        promptFragment:
          "Use casual language with contractions. Speak like a real person, not a corporate script.",
      },
      {
        id: "professional",
        label: "Professional",
        description: "Polished but human.",
        examplePreview: "Absolutely — I'll take care of that for you right away.",
        promptFragment: "Use clear, professional language. Friendly but never sloppy.",
      },
      {
        id: "formal",
        label: "Formal",
        description: "Proper, structured, no contractions.",
        examplePreview: "Certainly. I will arrange that for you immediately.",
        promptFragment:
          "Use formal language. Avoid contractions. Maintain a structured, respectful tone.",
      },
    ],
  },
];

export const personalityPresets: PersonalityPreset[] = [
  {
    id: "the-coach",
    label: "The Coach",
    description: "Energetic trainer who motivates.",
    genieIcon: "lightbulb",
    values: {
      role: "coach",
      length: "balanced",
      energy: "excited",
      approach: "empathetic",
      formality: "casual",
    },
  },
  {
    id: "the-concierge",
    label: "The Concierge",
    description: "Polished, polite, gets things done.",
    genieIcon: "smile",
    values: {
      role: "concierge",
      length: "balanced",
      energy: "warm",
      approach: "direct",
      formality: "professional",
    },
  },
  {
    id: "the-expert",
    label: "The Expert",
    description: "Authoritative, precise, knowledgeable.",
    genieIcon: "brain",
    values: {
      role: "expert",
      length: "detailed",
      energy: "calm",
      approach: "direct",
      formality: "professional",
    },
  },
  {
    id: "the-friend",
    label: "The Friend",
    description: "Warm, casual, easy to talk to.",
    genieIcon: "chat",
    values: {
      role: "friend",
      length: "short",
      energy: "warm",
      approach: "empathetic",
      formality: "casual",
    },
  },
];

export const knowledgeSources: KnowledgeSource[] = [
  {
    id: "documents",
    label: "Document library",
    description: "PDFs, docs, and files you've uploaded.",
    icon: FileText,
  },
  {
    id: "website",
    label: "Website pages",
    description: "Pages crawled from your website.",
    icon: Globe,
  },
  {
    id: "faq",
    label: "FAQ collection",
    description: "Q&A pairs you've curated.",
    icon: MessageCircle,
  },
  {
    id: "voice-notes",
    label: "Voice notes",
    description: "Transcribed voice memos and recordings.",
    icon: Mic,
  },
  {
    id: "team",
    label: "Team context",
    description: "Who's on the team and what they do.",
    icon: Users,
  },
];

export const sampleTestPrompts = [
  "Hi, what can you help me with?",
  "Can you walk me through getting started?",
  "I'm having trouble — can you help?",
  "Tell me about pricing.",
  "Can I book a call with someone?",
];
